import cx from 'classnames';
import {DateTime} from 'luxon';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import {selectStart, selectTimezoneName} from '~/CreateFabricInvitation/reducers/details/selectors';
import {DateTimeControl} from '~/common/components/StartEndController/DateTimeControl';
import {SET_GUEST_OPTIONS} from '~/common/fabric/reducers/guestOptions/constants';
import {
  formatDateForBackend,
  formatDateForDisplay,
  mergeDateAndTime,
} from '~/common/utils/date_utils';
import {Button} from '~library/atoms/Button';
import {Checkbox} from '~library/atoms/Checkbox';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {CalendarAltIcon} from '~library/svg/v2/CalendarAltIcon';
import {defaultTimes} from '~library/utils/constants';
import {pebble} from '~libSass/base/_exports.sass';

import './RSVPDeadlinePicker.sass';

interface RSVPDeadlinePickerProps {
  rsvpDeadline?: string;
  rsvpDeadlineIsStrict?: boolean;
  isGuestOptionsPage: boolean;
}

interface PickerPosition {
  top?: number;
  left?: number;
}

export const RSVPDeadlinePicker = ({
  rsvpDeadline,
  rsvpDeadlineIsStrict,
  isGuestOptionsPage,
}: RSVPDeadlinePickerProps) => {
  const dispatch = useDispatch();
  const [dateTimeOpen, setDateTimeOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [pickerPosition, setPickerPosition] = useState<PickerPosition>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const isLargishAndUp = useIsLargishAndUp();
  const rsvpDeadlineRef = useRef(rsvpDeadline);
  const eventStartDate = useSelector(selectStart);
  const timezone = useSelector(selectTimezoneName);

  const buttonText = useMemo(() => {
    if (rsvpDeadline) {
      return formatDateForDisplay(DateTime.fromISO(rsvpDeadline, {zone: timezone}));
    }
    return 'Pick a date & time';
  }, [rsvpDeadline, timezone]);

  const pickerStyle = {
    top: pickerPosition?.top,
    left: pickerPosition?.left,
  };

  useEffect(() => {
    rsvpDeadlineRef.current = rsvpDeadline;
  }, [rsvpDeadline]);

  const openDateTime = useCallback(() => {
    const containerBox = containerRef.current?.getBoundingClientRect();
    setPickerPosition({top: containerBox?.top, left: containerBox?.left});
    setShowError(false);
    setDateTimeOpen(true);
  }, []);

  const closeDateTime = useCallback(() => {
    if (!rsvpDeadlineRef.current) {
      setShowError(true);
    }
    setDateTimeOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const elem = event.target as Element;
      if (!elem.matches('.rsvp-deadline__date-time, .rsvp-deadline__date-time *')) {
        dateTimeOpen && closeDateTime();
      }
    };

    const handleWindowBlur = () => dateTimeOpen && closeDateTime();

    if (isLargishAndUp) {
      document.body.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleWindowBlur);
    }

    return () => {
      document.body.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleWindowBlur);
    };
  }, [dateTimeOpen, isLargishAndUp, closeDateTime]);

  const handleDateChange = useCallback(
    (date: Date) => {
      if (!date) return;
      const existingTime = rsvpDeadline
        ? DateTime.fromISO(rsvpDeadline).toFormat('h:mm a')
        : '6:00 PM';
      const newDateTime = mergeDateAndTime(date, existingTime);

      dispatch({
        type: SET_GUEST_OPTIONS,
        payload: {rsvpDeadline: formatDateForBackend(newDateTime)},
      });
    },
    [dispatch, rsvpDeadline]
  );

  const handleTimeChange = useCallback(
    (time: string) => {
      if (!time) return;
      const existingDate = rsvpDeadline ? DateTime.fromISO(rsvpDeadline).toJSDate() : new Date();
      const updatedDateTime = mergeDateAndTime(existingDate, time);

      dispatch({
        type: SET_GUEST_OPTIONS,
        payload: {rsvpDeadline: formatDateForBackend(updatedDateTime)},
      });
    },
    [dispatch, rsvpDeadline]
  );

  const handleToggleAfterDeadline = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: SET_GUEST_OPTIONS,
        payload: {rsvpDeadlineIsStrict: !e.target.checked},
      });
    },
    [dispatch]
  );

  const checkboxChecked = useMemo(() => {
    if (!rsvpDeadlineIsStrict && rsvpDeadlineIsStrict !== false) {
      return false;
    }
    return !rsvpDeadlineIsStrict;
  }, [rsvpDeadlineIsStrict]);

  return (
    <div className="rsvp-deadline" ref={containerRef}>
      <div className="rsvp-deadline__trigger-wrapper">
        <Button
          className={cx('rsvp-deadline__trigger', {
            'rsvp-deadline__trigger-error': showError,
          })}
          data-qa-id="rsvp-deadline-trigger"
          variant="unstyled-button"
          onClick={openDateTime}
          fullWidth
        >
          <CalendarAltIcon ratio={0.5} color={pebble} />
          <Typography variant="paragraph2" color={rsvpDeadline ? 'charcoal' : 'pebble'}>
            {buttonText}
          </Typography>
        </Button>
        {showError && (
          <Typography variant="paragraph4" color="feedbackErrorText">
            Please select a date and time.
          </Typography>
        )}
      </div>
      {dateTimeOpen && isLargishAndUp ? (
        createPortal(
          <div
            className={cx('rsvp-deadline__date-time', {
              'rsvp-deadline__date-time--bottom': isGuestOptionsPage,
            })}
            style={pickerStyle}
          >
            <DateTimeControl
              selectedTab="start"
              selectedDateTime={rsvpDeadline ? DateTime.fromISO(rsvpDeadline) : null}
              minDate={DateTime.now()}
              maxDate={eventStartDate ? DateTime.fromISO(eventStartDate) : undefined}
              times={defaultTimes}
              onChangeDate={handleDateChange}
              onChangeTime={handleTimeChange}
              open={true}
            />
          </div>,
          document.body
        )
      ) : (
        <BottomSheet
          in={dateTimeOpen}
          className="rsvp-deadline__bottom-sheet"
          title="Pick a date & time"
          onClose={closeDateTime}
          showBackBtn
          onBackBtnClick={closeDateTime}
          content={
            <>
              <DateTimeControl
                selectedTab="start"
                selectedDateTime={rsvpDeadline ? DateTime.fromISO(rsvpDeadline) : null}
                minDate={DateTime.now()}
                maxDate={eventStartDate ? DateTime.fromISO(eventStartDate) : undefined}
                times={defaultTimes}
                onChangeDate={handleDateChange}
                onChangeTime={handleTimeChange}
                open={true}
              />
              <Button
                className="rsvp-deadline__save-btn"
                data-qa-id="save-rsvp-deadline"
                onClick={closeDateTime}
                size="large"
                fullWidth
              >
                Save
              </Button>
            </>
          }
        />
      )}
      <Checkbox
        id="allow-rsvp-after-deadline"
        data-qa-id="allow-rsvp-after-deadline-checkbox"
        onChange={handleToggleAfterDeadline}
        checked={checkboxChecked}
        label="Allow guests to RSVP after the deadline"
        labelVariant="paragraph3"
      />
    </div>
  );
};
